/**
 * Error Aggregation Service
 * Aggregates similar errors to reduce noise and identify patterns
 */

import { supabase } from './supabase'
import { maskSensitiveData } from '../utils/dataMasking'

/**
 * Error fingerprint for grouping similar errors
 */
const getErrorFingerprint = (error, context = {}) => {
  const message = error?.message || 'Unknown error'
  const stack = error?.stack || ''
  
  // Extract key parts of stack trace (first few lines)
  const stackLines = stack.split('\n').slice(0, 3).join('\n')
  
  // Create fingerprint from message and stack
  const fingerprint = `${message}|${stackLines}`
  
  // Add context if relevant
  if (context.component) {
    return `${fingerprint}|${context.component}`
  }
  
  return fingerprint
}

/**
 * Error aggregation class
 */
class ErrorAggregator {
  constructor() {
    this.errorGroups = new Map() // fingerprint -> error group
    this.errorCounts = new Map() // fingerprint -> count
    this.lastFlush = Date.now()
    this.flushInterval = 60000 // 1 minute
  }

  /**
   * Aggregate error
   */
  aggregateError(error, context = {}) {
    const fingerprint = getErrorFingerprint(error, context)
    const now = Date.now()

    // Get or create error group
    if (!this.errorGroups.has(fingerprint)) {
      this.errorGroups.set(fingerprint, {
        fingerprint,
        message: error?.message || 'Unknown error',
        stack: error?.stack || '',
        firstSeen: now,
        lastSeen: now,
        count: 0,
        contexts: [],
        severity: context.severity || 'error',
        component: context.component,
        url: context.url || window.location.href,
      })
      this.errorCounts.set(fingerprint, 0)
    }

    const group = this.errorGroups.get(fingerprint)
    group.count++
    group.lastSeen = now
    this.errorCounts.set(fingerprint, group.count)

    // Add context (keep last 5 unique contexts)
    const contextKey = JSON.stringify({
      component: context.component,
      action: context.action,
      url: context.url,
    })
    
    if (!group.contexts.some((c) => JSON.stringify(c) === contextKey)) {
      group.contexts.push({
        component: context.component,
        action: context.action,
        url: context.url,
        timestamp: now,
      })
      if (group.contexts.length > 5) {
        group.contexts.shift()
      }
    }

    // Flush if interval passed
    if (now - this.lastFlush > this.flushInterval) {
      this.flush()
    }

    return {
      fingerprint,
      count: group.count,
      isNew: group.count === 1,
    }
  }

  /**
   * Flush aggregated errors to database
   */
  async flush(organizationId = null, userId = null) {
    if (this.errorGroups.size === 0) return

    const errorsToFlush = Array.from(this.errorGroups.values())
    this.lastFlush = Date.now()

    // Store in database if organization/user available
    if (organizationId && userId) {
      try {
        const errorRecords = errorsToFlush.map((group) => ({
          organization_id: organizationId,
          user_id: userId,
          error_fingerprint: group.fingerprint,
          error_message: maskSensitiveData(group.message, { patterns: ['all'] }),
          error_stack: maskSensitiveData(group.stack, { patterns: ['all'] }),
          error_count: group.count,
          first_seen: new Date(group.firstSeen).toISOString(),
          last_seen: new Date(group.lastSeen).toISOString(),
          severity: group.severity,
          component: group.component || null,
          url: group.url || null,
          contexts: group.contexts,
        }))

        // Upsert errors (update if exists, insert if new)
        const { error } = await supabase
          .from('error_aggregations')
          .upsert(errorRecords, {
            onConflict: 'error_fingerprint,organization_id',
            ignoreDuplicates: false,
          })

        if (error) {
          console.error('Failed to flush error aggregations:', error)
        }
      } catch (error) {
        console.error('Error flushing aggregations:', error)
      }
    }

    // Store in localStorage as backup
    try {
      const stored = JSON.parse(localStorage.getItem('error_aggregations') || '[]')
      const combined = [...stored, ...errorsToFlush]
      
      // Keep only last 100 groups
      if (combined.length > 100) {
        combined.splice(0, combined.length - 100)
      }
      
      localStorage.setItem('error_aggregations', JSON.stringify(combined))
    } catch (e) {
      console.error('Failed to store error aggregations:', e)
    }

    // Clear in-memory groups
    this.errorGroups.clear()
    this.errorCounts.clear()

    return errorsToFlush
  }

  /**
   * Get aggregated errors
   */
  getAggregatedErrors(limit = 20) {
    return Array.from(this.errorGroups.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, limit)
  }

  /**
   * Get error count for fingerprint
   */
  getErrorCount(fingerprint) {
    return this.errorCounts.get(fingerprint) || 0
  }

  /**
   * Clear aggregations
   */
  clear() {
    this.errorGroups.clear()
    this.errorCounts.clear()
  }
}

// Singleton instance
const errorAggregator = new ErrorAggregator()

/**
 * Aggregate error
 */
export const aggregateError = (error, context = {}) => {
  return errorAggregator.aggregateError(error, context)
}

/**
 * Flush aggregations
 */
export const flushAggregations = async (organizationId = null, userId = null) => {
  return errorAggregator.flush(organizationId, userId)
}

/**
 * Get aggregated errors
 */
export const getAggregatedErrors = (limit = 20) => {
  return errorAggregator.getAggregatedErrors(limit)
}

/**
 * Get error count
 */
export const getErrorCount = (fingerprint) => {
  return errorAggregator.getErrorCount(fingerprint)
}

export default errorAggregator

